import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import companyService from '../services/company.service';
import { jobService } from '../services/job.service';
import { toast } from 'react-toastify';
import { Button, Card, CardHeader, EmptyState, FormField, Icon, Input, Modal, Select } from '../ds';
import { Page, PageHeader, Loading, Note } from '../components/page';

// Fields side by side on a wide screen, stacked on a narrow one (the People
// app's form grid).
const formGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, alignItems: 'start' };

// The design system's Input hands back the change event without the field's
// name; give it one so handleInputChange stays as it is.
const named = (name, handler) => (e) => handler({ target: { name, value: e.target.value } });

/* Native fields, drawn like the design system's Input, where it cannot carry
   what the field needs (a datalist, min/max, type=date, a native select). */
const labelStyle = { fontSize: 'var(--fs-body-sm)', fontWeight: 500, color: 'var(--text-1)' };
const nativeStyle = (error) => ({
  width: '100%', minHeight: 40, padding: '0 14px', boxSizing: 'border-box',
  borderRadius: 'var(--radius-md)', border: `1px solid ${error ? 'var(--status-danger-text)' : 'var(--border-strong)'}`,
  background: 'var(--surface-1)', color: 'var(--text-1)', font: 'inherit', fontSize: 'var(--fs-body-md)',
});
const NATIVE_FOCUS = '.jd-native:focus{outline:none;border-color:var(--focus-ring-color);box-shadow:var(--focus-ring-shadow)}';

/* Label, native control, then the error or the hint: the design system's
   field layout, with the label linked to the control by id. */
const NativeField = ({ id, label, error, hint, children }) => (
  <div style={{ display: 'grid', gap: 6 }}>
    <label htmlFor={id} style={labelStyle}>{label}</label>
    {children}
    {(error || hint) && (
      <span style={{ fontSize: 'var(--fs-caption)', color: error ? 'var(--status-danger-text)' : 'var(--text-muted)' }}>{error || hint}</span>
    )}
  </div>
);

/* Today in India, as YYYY-MM-DD: the earliest closing date the form allows
   (a vacancy closes at the end of its last day, India time). */
const todayInIndia = () => new Date(Date.now() + 5.5 * 60 * 60 * 1000).toISOString().slice(0, 10);

const EMPTY_ERRORS = {
  jobTitle: '', department: '', subject: '', gradeBand: '', openings: '', minExperienceYears: '',
  closingDate: '', qualifications: '', responsibilities: '', companyId: '',
};

// @story HIR-HR1
const CreateJobDescription = () => {
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  /* Every field HIR-HR1 names, and every field the connector's draft_vacancy
     takes: the server reads both through the same function. */
  const [formData, setFormData] = useState({
    jobTitle: '',
    department: '',
    subject: '',
    gradeBand: '',
    openings: 1,
    employmentType: 'Full-time',
    minExperienceYears: '',
    closingDate: '',
    qualifications: '',
    responsibilities: '',
    requirements: '',
    companyId: '' // Add company ID field
  });

  // Add validation state
  const [errors, setErrors] = useState(EMPTY_ERRORS);

  const [loading, setLoading] = useState(false);
  /* The choices come from the server (GET /jobs/vacancy-options): one list,
     shared with the careers page and the connector. No copy is kept here or in
     the browser, so a department cannot exist on this form and nowhere else. */
  const [options, setOptions] = useState(null);
  const [optionsError, setOptionsError] = useState('');

  // Company selection state
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [showCompanyModal, setShowCompanyModal] = useState(false);

  const loadOptions = async () => {
    setOptionsError('');
    try {
      setOptions(await jobService.getVacancyOptions());
    } catch (e) {
      setOptionsError('Could not load the departments and grade bands.');
    }
  };

  useEffect(() => {
    loadOptions();
  }, []);

  const departments = options?.departments || [];
  const isTeaching = (dept) => (options?.teachingDepartments || []).includes(dept);

  // Load companies on component mount
  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setCompaniesLoading(true);
      const response = await companyService.getAllCompanies();
      if (response.success) {
        const list = response.data || [];
        setCompanies(list);
        // A school is its own — and usually only — "company". When there is
        // exactly one, pick it automatically so the person never has to
        // understand or choose a company just to post a JD.
        if (list.length === 1) {
          setFormData((prev) => ({ ...prev, companyId: list[0]._id }));
        }
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setCompaniesLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = { ...EMPTY_ERRORS };

    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = 'Job title is required';
    }

    if (!formData.department) {
      newErrors.department = 'Please select a department';
    }

    // A teaching post is for a subject and a grade band (HIR-HR1).
    if (isTeaching(formData.department)) {
      if (!formData.subject.trim()) newErrors.subject = 'Subject is required for a teaching post';
      if (!formData.gradeBand) newErrors.gradeBand = 'Grade band is required for a teaching post';
    }

    const openings = Number(formData.openings);
    if (!Number.isInteger(openings) || openings < 1 || openings > 100) {
      newErrors.openings = 'A whole number from 1 to 100';
    }

    if (formData.minExperienceYears !== '') {
      const years = Number(formData.minExperienceYears);
      if (!Number.isInteger(years) || years < 0 || years > 40) {
        newErrors.minExperienceYears = 'Whole years, 0 to 40';
      }
    }

    if (formData.closingDate && formData.closingDate < todayInIndia()) {
      newErrors.closingDate = 'The closing date is already past';
    }

    if (!formData.qualifications.trim()) {
      newErrors.qualifications = 'Qualifications are required';
    }

    if (!formData.responsibilities.trim()) {
      newErrors.responsibilities = 'Responsibilities are required';
    }

    if (!formData.companyId) {
      newErrors.companyId = 'Please select a company';
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleDepartmentSelect = (dept) => {
    setFormData(prev => ({
      ...prev,
      department: dept
    }));
    setErrors(prev => ({
      ...prev,
      department: ''
    }));
  };

  const handleCompanySelect = (companyId) => {
    setFormData(prev => ({
      ...prev,
      companyId: companyId
    }));
    setErrors(prev => ({
      ...prev,
      companyId: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { minExperienceYears, openings, ...rest } = formData;
      const response = await jobService.createJob({
        ...rest,
        openings: Number(openings),
        // Left empty = not stated, which is different from 0 (freshers welcome).
        ...(minExperienceYears !== '' ? { minExperienceYears: Number(minExperienceYears) } : {}),
        companyId: formData.companyId // Include company ID in the request
      });

      if (response.data) {
        navigate(`/jobs/${response.data._id}`);
      } else {
        setError('Failed to create job');
      }
    } catch (error) {
      console.error('Error creating job:', error);
      setError(error?.response?.data?.message || error?.message || 'An error occurred while creating the job');
    } finally {
      setLoading(false);
    }
  };


  const teaching = isTeaching(formData.department);

  return (
    <Page>
      <style>{NATIVE_FOCUS}</style>
      <PageHeader
        title="Create a job description"
        subtitle="Enter the job details; the hiring assistant writes a Job Description & KRA tailored to your school."
      />

      <form onSubmit={handleSubmit} noValidate style={{ display: 'grid', gap: 24 }}>
        {error && <Note tone="danger">{error}</Note>}

        {/* Company: a school is usually its own and only one, chosen for them. */}
        <Card>
          <CardHeader
            title={companies.length === 1 ? 'Company' : 'Select Company'}
            subtitle={companies.length === 1 ? 'Where the vacancy is posted.' : 'Who the vacancy is for.'}
            // Only offer "Add Company" when there is a real choice to make.
            action={companies.length !== 1 && !companiesLoading ? (
              <Button size="sm" variant="ghost" icon={<Icon name="plus" />} onClick={() => setShowCompanyModal(true)}>
                Add Company
              </Button>
            ) : null}
          />
          {companiesLoading ? (
            <Loading label="Loading companies" />
          ) : companies.length === 1 ? (
            /* The school itself, chosen for them — shown, not asked. */
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--text-2)' }}>
              <Icon name="house-line" size={18} />
              <span>Posting for <strong style={{ color: 'var(--text-1)', fontWeight: 600 }}>{companies[0].name}</strong></span>
            </div>
          ) : companies.length === 0 ? (
            <EmptyState
              icon={<Icon name="house-line" size={36} />}
              title="No companies found"
              action={<Button variant="secondary" onClick={() => setShowCompanyModal(true)}>Add your first company</Button>}
            />
          ) : (
            <FormField label="Company" required error={errors.companyId || undefined}>
              <Select
                placeholder="Select a company"
                value={formData.companyId}
                onChange={handleCompanySelect}
                options={companies.map((c) => ({ value: c._id, label: c.industry ? `${c.name} · ${c.industry}` : c.name }))}
              />
            </FormField>
          )}
          {/* With one company or none there is no field to hang the message on. */}
          {errors.companyId && !companiesLoading && companies.length <= 1 && (
            <div style={{ marginTop: 12 }}><Note tone="danger">{errors.companyId}</Note></div>
          )}
        </Card>

        {/* Step 1: the job itself. */}
        <Card>
          <CardHeader title="Enter Job Details" subtitle="Title, department, subject and grade band." />
          <div style={{ display: 'grid', gap: 16 }}>
            <div style={formGrid}>
              <Input
                id="jd-title"
                label="Job Title"
                required
                placeholder="Enter Job Title"
                value={formData.jobTitle}
                onChange={named('jobTitle', handleInputChange)}
                error={errors.jobTitle || undefined}
              />

              {/* Department: the one list the careers page and connector use too.
                  A new department is added there (config/vacancyOptions.js), not
                  here, so it reaches every surface at once. */}
              <FormField label="Select Department" required error={errors.department || undefined}>
                {optionsError ? (
                  <Note tone="danger">
                    <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      {optionsError}
                      <Button size="sm" variant="secondary" onClick={loadOptions}>Try again</Button>
                    </span>
                  </Note>
                ) : !options ? (
                  <span style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)' }}>Loading departments…</span>
                ) : (
                  <Select
                    placeholder="Select a department"
                    value={formData.department}
                    onChange={handleDepartmentSelect}
                    options={departments.map((d) => ({ value: d, label: d }))}
                  />
                )}
              </FormField>
            </div>

            {/* Subject and grade band: asked for on a teaching post */}
            <div style={formGrid}>
              <NativeField id="jd-subject" label={`Subject${teaching ? '' : ' (optional)'}`} error={errors.subject}>
                <input
                  type="text"
                  id="jd-subject"
                  name="subject"
                  list="subject-suggestions"
                  className="jd-native"
                  style={nativeStyle(errors.subject)}
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder={formData.department ? 'Type or pick a subject' : 'Pick a department first'}
                  maxLength={60}
                />
                <datalist id="subject-suggestions">
                  {(options?.subjects?.[formData.department] || []).map((s) => <option key={s} value={s} />)}
                </datalist>
              </NativeField>
              <NativeField id="jd-grade-band" label={`Grade band${teaching ? '' : ' (optional)'}`} error={errors.gradeBand}>
                <select
                  id="jd-grade-band"
                  name="gradeBand"
                  className="jd-native"
                  style={nativeStyle(errors.gradeBand)}
                  value={formData.gradeBand}
                  onChange={handleInputChange}
                >
                  <option value="">Choose a grade band</option>
                  {(options?.gradeBands || []).map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </NativeField>
            </div>
          </div>
        </Card>

        {/* How the post is held */}
        <Card>
          <CardHeader title="The post" subtitle="Openings, employment type, experience and closing date." />
          <div style={formGrid}>
            <NativeField id="jd-openings" label="Number of openings" error={errors.openings}>
              <input
                type="number"
                id="jd-openings"
                name="openings"
                min={1}
                max={100}
                step={1}
                className="jd-native"
                style={nativeStyle(errors.openings)}
                value={formData.openings}
                onChange={handleInputChange}
              />
            </NativeField>
            <NativeField id="jd-employment-type" label="Employment type">
              <select
                id="jd-employment-type"
                name="employmentType"
                className="jd-native"
                style={nativeStyle(false)}
                value={formData.employmentType}
                onChange={handleInputChange}
              >
                {(options?.employmentTypes || ['Full-time']).map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </NativeField>
            <NativeField id="jd-experience" label="Experience (min. years)" error={errors.minExperienceYears}>
              <input
                type="number"
                id="jd-experience"
                name="minExperienceYears"
                min={0}
                max={40}
                step={1}
                className="jd-native"
                style={nativeStyle(errors.minExperienceYears)}
                value={formData.minExperienceYears}
                onChange={handleInputChange}
                placeholder="0 = freshers"
              />
            </NativeField>
            <NativeField
              id="jd-closing-date"
              label="Closing date (optional)"
              error={errors.closingDate}
              hint="Applications close at the end of this day (India time); the role then leaves the careers page."
            >
              <input
                type="date"
                id="jd-closing-date"
                name="closingDate"
                min={todayInIndia()}
                className="jd-native"
                style={nativeStyle(errors.closingDate)}
                value={formData.closingDate}
                onChange={handleInputChange}
              />
            </NativeField>
          </div>
        </Card>

        {/* What the post asks for, and what it involves. One item per line. */}
        <Card>
          <CardHeader title="Qualifications and responsibilities" subtitle="One per line." />
          <div style={{ display: 'grid', gap: 16 }}>
            <Input
              id="jd-qualifications"
              label="Qualifications"
              required
              multiline
              rows={4}
              placeholder={'e.g. B.Sc. with Environmental Science\nB.Ed.'}
              value={formData.qualifications}
              onChange={named('qualifications', handleInputChange)}
              error={errors.qualifications || undefined}
              hint="One per line"
            />
            {/* Other requirements: separate from qualifications (HIR-HR1) */}
            <Input
              id="jd-requirements"
              label="Other requirements (optional)"
              multiline
              rows={3}
              placeholder={'e.g. CTET Paper II\nFluent in English and Hindi'}
              value={formData.requirements}
              onChange={named('requirements', handleInputChange)}
              hint="One per line. Shown on the job description exactly as you write them."
            />
            <Input
              id="jd-responsibilities"
              label="Responsibilities"
              required
              multiline
              rows={4}
              placeholder={'e.g. Teach Environmental Science to Grades 6 to 8\nTake students on nature walks'}
              value={formData.responsibilities}
              onChange={named('responsibilities', handleInputChange)}
              error={errors.responsibilities || undefined}
              hint="One per line"
            />
          </div>
        </Card>

        {/* Step 2: the assistant writes the JD & KRA from the above. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Button type="submit" loading={loading} icon={<Icon name="sparkle" />}>
            {loading ? 'Generating...' : 'Generate'}
          </Button>
          <span style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)' }}>
            The hiring assistant generates a custom Job Description & KRA from these details.
          </span>
        </div>
      </form>

      {/* Companies are added on their own page. */}
      <Modal open={showCompanyModal} title="Add New Company" onClose={() => setShowCompanyModal(false)}>
        <EmptyState
          icon={<Icon name="house-line" size={36} />}
          title="Add Company"
          description="Please add a company first to create jobs"
          action={(
            <div>
              <Button onClick={() => { setShowCompanyModal(false); navigate('/companies'); }}>
                Go to Companies
              </Button>
            </div>
          )}
        />
      </Modal>
    </Page>
  );
};

export default CreateJobDescription;
